import { useState, useEffect } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import logo from '../t-accounts-logo.svg';

// Configure which OAuth providers are enabled
const ENABLED_PROVIDERS = [
  { id: 'azure', name: 'Microsoft', icon: MicrosoftIcon },
  // { id: 'google', name: 'Google', icon: GoogleIcon },
  // { id: 'apple', name: 'Apple', icon: AppleIcon },
  // { id: 'linkedin_oidc', name: 'LinkedIn', icon: LinkedInIcon },
];

type AuthMode = 'signin' | 'signup' | 'forgot' | 'reset';

function normalizeMode(mode: string | null): AuthMode {
  if (mode === 'signup' || mode === 'forgot' || mode === 'reset') {
    return mode;
  }

  return 'signin';
}

function normalizeAuthErrorMessage(message: string): string {
  const normalized = message.trim().toLowerCase();

  if (
    normalized.includes('suspended')
    || normalized.includes('paused')
    || normalized.includes('project is disabled')
    || normalized.includes('project has been paused')
  ) {
    return 'Authentication is temporarily unavailable because the Supabase project is suspended. Please reactivate the project and try again.';
  }

  if (normalized.includes('failed to fetch')) {
    return 'Unable to reach the authentication service. Check your network connection and try again.';
  }

  if (normalized.includes('signal is aborted without reason') || normalized.includes('aborterror')) {
    return 'Authentication request timed out. Please try again.';
  }

  return message;
}

export default function Auth() {
  const [searchParams] = useSearchParams();
  const requestedMode = normalizeMode(searchParams.get('mode'));
  const hasRecoveryHash = window.location.hash.toLowerCase().includes('type=recovery');
  const mode: AuthMode = requestedMode === 'signin' && hasRecoveryHash ? 'reset' : requestedMode;
  const isSignUp = mode === 'signup';
  const isForgot = mode === 'forgot';
  const isReset = mode === 'reset';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [resetSessionReady, setResetSessionReady] = useState(false);
  const [resetSessionChecking, setResetSessionChecking] = useState(false);
  const navigate = useNavigate();
  
  const {
    signIn,
    signUp,
    requestPasswordReset,
    updatePassword,
    signInWithProvider,
  } = useAuth();

  useEffect(() => {
    // Clear form when mode changes
    setError('');
    setMessage('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
  }, [mode]);

  useEffect(() => {
    if (!isReset) {
      setResetSessionReady(false);
      setResetSessionChecking(false);
      return;
    }

    let isMounted = true;
    setResetSessionChecking(true);

    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) {
        return;
      }

      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setResetSessionReady(Boolean(session));
      }
    });

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        if (isMounted) {
          setResetSessionReady(Boolean(session));
        }
      })
      .catch(() => {
        if (isMounted) {
          setResetSessionReady(false);
        }
      })
      .finally(() => {
        if (isMounted) {
          setResetSessionChecking(false);
        }
      });

    return () => {
      isMounted = false;
      data.subscription.unsubscribe();
    };
  }, [isReset]);

  const handleEmailSignIn = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error } = await signIn(email, password);

      if (error) {
        setError(normalizeAuthErrorMessage(error.message));
      } else {
        // Navigate to documents page on successful sign in
        navigate('/');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign in failed';
      setError(normalizeAuthErrorMessage(message));
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSignUp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const { error } = await signUp(email, password);

      if (error) {
        setError(normalizeAuthErrorMessage(error.message));
      } else {
        setMessage('Check your email for the confirmation link!');
        setEmail('');
        setPassword('');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sign up failed';
      setError(normalizeAuthErrorMessage(message));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const { error } = await requestPasswordReset(email);

      if (error) {
        setError(normalizeAuthErrorMessage(error.message));
      } else {
        setMessage('If an account exists for that email, a password reset link has been sent.');
        setEmail('');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset request failed';
      setError(normalizeAuthErrorMessage(message));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!resetSessionReady) {
      setError('Your reset link is invalid or expired. Request a new password reset email.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const { error } = await updatePassword(password);

      if (error) {
        setError(normalizeAuthErrorMessage(error.message));
      } else {
        setMessage('Password updated successfully.');
        setPassword('');
        setConfirmPassword('');
        navigate('/');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password update failed';
      setError(normalizeAuthErrorMessage(message));
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider) => {
    setError('');
    setLoading(true);

    try {
      const { error } = await signInWithProvider(provider);

      if (error) {
        setError(normalizeAuthErrorMessage(error.message));
      } else {
        // OAuth will redirect, but if it doesn't, navigate manually
        navigate('/');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'OAuth sign in failed';
      setError(normalizeAuthErrorMessage(message));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/');
  };

  const title = isSignUp
    ? 'Sign up'
    : isForgot
      ? 'Reset your password'
      : isReset
        ? 'Choose a new password'
        : 'Sign in';

  const submitLabel = isSignUp
    ? 'Sign Up'
    : isForgot
      ? 'Send Reset Link'
      : isReset
        ? 'Update Password'
        : 'Sign In';

  const submitHandler = isSignUp
    ? handleEmailSignUp
    : isForgot
      ? handleForgotPassword
      : isReset
        ? handlePasswordReset
        : handleEmailSignIn;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f4f6f7',
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, position: 'relative' }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            disabled={loading}
            sx={{ position: 'absolute', top: 12, left: 12 }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img 
              src={logo} 
              alt="T-Accounts Logo" 
              style={{ height: 50 }} 
            />
          </Box>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
            {title}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {message && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
              {message}
            </Alert>
          )}

          <form onSubmit={submitHandler}>
            {!isReset && (
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={loading}
              />
            )}

            {!isForgot && (
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading || (isReset && !resetSessionReady)}
                helperText={isSignUp || isReset ? 'Must be at least 6 characters' : ''}
              />
            )}

            {isReset && (
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading || !resetSessionReady}
              />
            )}

            {isReset && resetSessionChecking && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Verifying your password reset link...
              </Alert>
            )}

            {isReset && !resetSessionChecking && !resetSessionReady && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Your reset link is invalid or expired. Request a new reset email.
              </Alert>
            )}

            {!isSignUp && !isForgot && !isReset && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => navigate('/auth?mode=forgot')}
                  disabled={loading}
                >
                  Forgot password?
                </Button>
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ mt: 2 }}
              disabled={loading || (isReset && !resetSessionReady)}
            >
              {loading ? <CircularProgress size={24} /> : submitLabel}
            </Button>
          </form>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            {isSignUp && (
              <Button size="small" onClick={() => navigate('/auth?mode=signin')} disabled={loading}>
                Already have an account? Sign in
              </Button>
            )}
            {!isSignUp && !isReset && (
              <Button size="small" onClick={() => navigate('/auth?mode=signup')} disabled={loading}>
                Need an account? Sign up
              </Button>
            )}
            {(isForgot || isReset) && (
              <Button size="small" onClick={() => navigate('/auth?mode=signin')} disabled={loading}>
                Back to sign in
              </Button>
            )}
          </Box>

          {ENABLED_PROVIDERS.length > 0 && !isForgot && !isReset && (
            <>
              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  OR
                </Typography>
              </Divider>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {ENABLED_PROVIDERS.map((provider) => {
                  const IconComponent = provider.icon;
                  return (
                    <Button
                      key={provider.id}
                      fullWidth
                      variant="outlined"
                      startIcon={<IconComponent />}
                      onClick={() => handleOAuthSignIn(provider.id)}
                      disabled={loading}
                    >
                      Continue with {provider.name}
                    </Button>
                  );
                })}
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
